import { createContext, useContext } from 'react';
import type {
  CrmNotification,
  CrmNotificationType,
  NotificationMute,
  NotificationPreferences,
} from '../../api/types';
import { channelLabel } from '../../lib/channelMeta';
import type { PermissionState } from '../../lib/webNotify';

/**
 * Context + helpers for per-agent notifications, apart from the provider so
 * react-refresh sees component-only modules (same split as context.ts /
 * CommsProvider.tsx).
 */

/** 'WhatsApp', from the snapshot or derived for rows stamped before it carried one. */
export function notificationChannel(n: CrmNotification): string | null {
  return n.snapshot.channelLabel ?? channelLabel(n.snapshot.channelType);
}

/** Who the conversation is with — the address when the contact has no name yet. */
export function notificationContact(n: CrmNotification): string | null {
  return n.snapshot.contactName || n.snapshot.contactIdentifier || null;
}

/**
 * The one line an OS banner gets, so it has to carry the channel: these land on
 * the same lock screen as the dispatch dashboard's own notifications, and
 * "New message from Jane" alone does not say which product it came from.
 */
export function notificationTitle(n: CrmNotification): string {
  const contact = notificationContact(n) ?? 'A customer';
  const channel = notificationChannel(n);
  switch (n.type) {
    case 'conversation_assigned':
      return `Assigned to you · ${contact}`;
    case 'bot_handoff':
      return `Bot handed off · ${contact}`;
    case 'conversation_created':
      return channel ? `New ${channel} conversation · ${contact}` : `New conversation · ${contact}`;
    default:
      return channel ? `New ${channel} message from ${contact}` : `New message from ${contact}`;
  }
}

/**
 * Where it landed: the channel, then the line it came in on. Channels are
 * routinely named after themselves ("Telegram", "WhatsApp Business"), and
 * "Telegram · Telegram" reads as a bug — so the channel is dropped when the name
 * already carries it. Mirrors origin() in services/notifications.js.
 */
export function notificationOrigin(n: CrmNotification): string {
  const channel = notificationChannel(n);
  const name = n.snapshot.inboxName;
  if (!name) return channel ?? '';
  if (!channel || name.toLowerCase().includes(channel.toLowerCase())) return name;
  return `${channel} · ${name}`;
}


export interface NotificationsState {
  /** Server-truth unread count (collapse-aware), kept in sync with local reads. */
  unread: number;
  /** Newest first; page 1 loads at mount, the panel pages further. */
  items: CrmNotification[];
  loading: boolean;
  hasMore: boolean;
  /** An older page is in flight. */
  loadingMore: boolean;
  loadMore: () => void;
  /** Bell hidden entirely: the role lacks crm_all (1036 on the API). */
  denied: boolean;
  /**
   * Framed by the dispatch dashboard, which lists these notifications itself
   * (its bell's CRM tab, read from `GET /notifications`) and toasts them on
   * every page. The module then draws no bell and raises no toasts of its own —
   * the same notification twice on one screen is what this replaced. Everything
   * else here still runs: the settings, the mutes, and marking a conversation's
   * notifications read when it is opened.
   */
  hostedByDashboard: boolean;
  toasts: CrmNotification[];
  dismissToast: (id: number) => void;
  markRead: (id: number) => void;
  markAllRead: () => void;
  /** Open the conversation a notification points at (and mark it read). */
  openNotification: (n: CrmNotification) => void;
  mutes: NotificationMute[];
  muteFor: (scope: 'inbox' | 'conversation', targetId: number | null) => NotificationMute | null;
  /** `until` null = permanent. Re-muting the same target updates the expiry. */
  mute: (body: {
    scope: 'inbox' | 'conversation';
    /** Defaults to 'mute'; 'watch' is the Notify-me inverse. */
    mode?: 'mute' | 'watch';
    inboxId?: number;
    conversationDisplayId?: number;
    until?: string | null;
  }) => Promise<void>;
  unmute: (muteId: number) => Promise<void>;
  /** null until the settings load (or when the fetch failed). */
  preferences: NotificationPreferences | null;
  /** Partial patch; merges server-side and resolves with the merged blob applied. */
  savePreferences: (patch: Partial<NotificationPreferences>) => Promise<void>;
  /**
   * Per-channel overrides of the type toggles, keyed by inbox id. Only the keys
   * the agent actually changed are present — everything else falls through to
   * `preferences`, then to the defaults.
   */
  inboxPreferences: Record<string, Partial<NotificationPreferences>>;
  saveInboxPreferences: (
    inboxId: number,
    patch: Partial<NotificationPreferences>,
  ) => Promise<void>;
  /** Drop the override so the channel follows the global settings again. */
  clearInboxPreferences: (inboxId: number) => Promise<void>;
  /** False when embedded or on a browser without the Notification API. */
  supportsPush: boolean;
  pushPermission: PermissionState;
  /** Prompts for OS notifications — call it from a click, never on mount. */
  enablePush: () => Promise<PermissionState>;
}


export const NotificationsContext = createContext<NotificationsState | null>(null);

export function useNotifications(): NotificationsState {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used inside NotificationsProvider');
  return ctx;
}

/** Shared duration menu for mute controls (null = forever). */
export const MUTE_OPTIONS: { label: string; hours: number | null }[] = [
  { label: 'For 1 hour', hours: 1 },
  { label: 'For 8 hours', hours: 8 },
  { label: 'For 24 hours', hours: 24 },
  { label: 'For 1 week', hours: 168 },
  { label: 'Until I unmute', hours: null },
];

export function muteUntil(hours: number | null): string | null {
  return hours === null ? null : new Date(Date.now() + hours * 3_600_000).toISOString();
}

export const NOTIFICATION_TYPE_LABELS: Record<CrmNotificationType, { title: string; sub: string }> =
  {
    conversation_assigned: {
      title: 'Assigned to me',
      sub: 'A conversation is assigned to you',
    },
    new_message: {
      title: 'New messages',
      sub: 'An incoming message on a conversation that reaches you — all of a channel, or only yours',
    },
    bot_handoff: {
      title: 'Bot handoffs',
      sub: 'The chatbot hands a conversation to a human',
    },
    conversation_created: {
      title: 'New conversations',
      sub: 'Any new conversation starts in your inboxes',
    },
  };
